<template>
    <div
        class="d-inline-flex mt-2"
        v-bind:style="styleObj"
        v-bind:class="{ 'w-100': inViewMode }">
        <div
            v-if="isRootFormula"
            class="ms-3 me-2"
            v-bind:class="rootClassObj">
            <span> = </span>
        </div>
        <template v-if="hasExpression">
            <component
                v-bind:is="expressionComponent"
                ref="root"
                v-bind:expression="formula.RootExpression"
                v-bind:data-test-role="'editorFormula' + editPath.toString()"
                v-bind:path="editPath" />
        </template>
        <template v-else>
            <edit-formula-expression-child
                ref="root"
                v-bind:child-binder="emptyChildBinder"
                v-bind:class="rootClassObj" />
        </template>
    </div>
</template>
<script>
    import editChild from "Formula/EditFormula/editChild.vue";
    import editExpression from "Formula/EditFormula/editExpression.vue";
    import expression from "./expression.vue";
    import child from "./child.vue";
    import { responsiveManager } from "Responsive/manager";
    import { VUE_ROOT_APP_SYMBOL } from "_Main/injectionSymbols";

    export const FORMULA_MODE = {
        toolbox: "toolbox",
        editor: "editor",
        view: "view"
    };

    export default {
        inject: {
            viewport: {
                from: "viewport",
                default: null
            },
            vueRootApp: VUE_ROOT_APP_SYMBOL
        },
        provide() {
            return {
                formulaControl: this
            };
        },
        props: {
            formula: { type: Object, required: true },
            simple: { type: Boolean, required: false, default: false },
            showSeparators: { type: Boolean, required: false, default: false },
            maxWidth: { type: Number, required: false, default: Number.MAX_SAFE_INTEGER },
            mode: { type: String, required: false, default: FORMULA_MODE.view },
            // It has index of editor formula node marking begining of the path
            // For toolbox Mode and View Mode, editPath defaults to []
            editPath: {
                type: Array,
                required: false,
                default: function () {
                    return [];
                }
            },
            position: { type: Object, required: false, default: null },
            isClone: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                expressions: []
            };
        },
        computed: {
            /**
             * Container "breakpoint". If this is contained within a viewport, use that
             * viewport's bp. Otherwise, use the global breakpoint. Controls wrapping
             * when container changes size.
             */
            bp() {
                if (this.viewport) {
                    return this.viewport.bp;
                } else {
                    return responsiveManager.breakpoint;
                }
            },
            hasExpression() {
                return this.formula && this.formula.RootExpression;
            },
            inViewMode() {
                return this.mode === FORMULA_MODE.view;
            },
            inEditMode() {
                return this.mode === FORMULA_MODE.editor;
            },
            inToolboxMode() {
                return this.mode === FORMULA_MODE.toolbox;
            },
            expressionComponent() {
                return this.inViewMode ? expression : editExpression;
            },
            childExpressionComponent() {
                return this.inViewMode ? child : editChild;
            },
            //this property is used to set rootClassObj if it is root node and shoe "=" for empty node
            isRootFormula() {
                return this.inEditMode && this.editPath[0] === this.$root.rootIndex;
            },
            rootClassObj() {
                return {
                    "d-inline-flex": this.isRootFormula,
                    "align-items-center": this.isRootFormula
                };
            },
            styleObj() {
                if (this.inViewMode) {
                    return { "overflow-x": "auto" };
                }
                return this.inEditMode && this.position
                    ? {
                          position: "absolute",
                          top: this.position.y,
                          left: this.position.x
                      }
                    : {};
            },
            isDroppable() {
                return !this.isClone && this.inEditMode && this.$root.isDragging;
            },
            emptyChildBinder() {
                return { path: [0] };
            }
        },
        watch: {
            bp() {
                // We wait for editor panel to render in UpdateSize
                // That sets maxWidth which in turn calls wrapping
                if (!this.inEditMode) {
                    this.wrap();
                }
            },
            async maxWidth() {
                await this.$nextTick();
                this.wrap();
            }
        },
        created() {
            if (!this.vueRootApp.component("formula-expression-child")) {
                this.vueRootApp.component("formula-expression-child", child);
            }
            if (!this.vueRootApp.component("edit-formula-expression-child")) {
                this.vueRootApp.component("edit-formula-expression-child", editChild);
            }
            if (!this.vueRootApp.component("formula-expression")) {
                this.vueRootApp.component("formula-expression", expression);
            }
            if (!this.vueRootApp.component("edit-formula-expression")) {
                this.vueRootApp.component("edit-formula-expression", editExpression);
            }
        },
        mounted() {
            // We wait for editor panel to render in UpdateSize
            // That sets maxWidth which in turn calls wrapping
            if (!this.inEditMode) {
                this.wrap();
            }
        },
        methods: {
            async wrap() {
                this.expressions.forEach((c) => (c.wrap = false));
                await this.$nextTick();

                const width = Math.min(this.maxWidth, Math.ceil(this.$el.getBoundingClientRect().width));
                const wrapCandidates = this.expressions.filter((c) => c.wrapGain() > 0);

                while (
                    this.$refs.root &&
                    Math.ceil(this.$refs.root.$el.getBoundingClientRect().width) > width &&
                    wrapCandidates.length > 0
                ) {
                    let wcWithMaxGain = null;
                    let wcIndex = null;
                    wrapCandidates.forEach((wc, index) => {
                        if (wcWithMaxGain == null) {
                            wcWithMaxGain = wc;
                            wcIndex = index;
                        } else {
                            if (wc.wrapGain() > wcWithMaxGain.wrapGain()) {
                                wcWithMaxGain = wc;
                                wcIndex = index;
                            }
                        }
                    });
                    wcWithMaxGain.wrap = true;
                    wrapCandidates.splice(wcIndex, 1);
                    await this.$nextTick();
                }
            },
            registerExpression(child) {
                this.expressions.push(child);
            },
            async timeout(ms) {
                await new Promise((resolve) => setTimeout(resolve, ms));
                await this.$nextTick();
            }
        }
    };
</script>
